import { useMemo } from 'react';
import RenderIf from 'components/RenderIf/RenderIf';
import { useAuthContext } from 'contexts/AuthContext';
import { useGetOrganizationUsers } from 'hooks/useGetOrganizationUsers';
import { IUser } from 'types/user';
import { useGetTimelineComments } from 'views/DealDashboard/Overview/Timeline/hooks/useGetTimelineComments';
import CommentComposer from './CommentComposer/CommentComposer';
import CommentThread from './CommentThread/CommentThread';

export interface TimelineSlideoutCommentsProps {
    orgId: string;
    dealId: number;
    timelineId: number;
    enabled: boolean;
}

const TimelineSlideoutComments = ({
    orgId, dealId, timelineId, enabled,
}: TimelineSlideoutCommentsProps) => {
    const { user } = useAuthContext();
    const { data: organizationUsers, isLoading: isLoadingUsers } = useGetOrganizationUsers(orgId, { enabled });
    const { data: comments, isLoading: isLoadingComments } = useGetTimelineComments({
        orgId, dealId, timelineId, enabled,
    });

    const isLoading = isLoadingComments || isLoadingUsers;

    const usersByCorrelationId = useMemo(
        () => new Map<string, IUser>((organizationUsers ?? []).map(orgUser => [orgUser.correlationId, orgUser])),
        [organizationUsers],
    );
    const currentUserCorrelationId = useMemo(
        () => organizationUsers?.find(orgUser => orgUser.id === user?.id)?.correlationId ?? null,
        [organizationUsers, user],
    );

    const threads = comments ?? [];

    return (
        <div className="p-3">
            <div className="mb-3">
                <CommentComposer orgId={orgId} dealId={dealId} timelineId={timelineId} />
            </div>
            <RenderIf isLoading={isLoading} isTrue>
                {threads.length === 0 ? (
                    <div className="text-muted">No comments yet.</div>
                ) : (
                    threads.map(thread => (
                        <CommentThread
                            key={thread.id}
                            thread={thread}
                            depth={0}
                            orgId={orgId}
                            dealId={dealId}
                            timelineId={timelineId}
                            usersByCorrelationId={usersByCorrelationId}
                            currentUserCorrelationId={currentUserCorrelationId}
                        />
                    ))
                )}
            </RenderIf>
        </div>
    );
};

export default TimelineSlideoutComments;
